import { ApiError, type ApiClient } from '../../api/client';
import type { AppAccessSummary, AppItem, Conversation, ConversationAccessSummary, ConversationMessage, WorkerNode } from '../../types';
import { browserSessionKey } from './client';

export type BrowserSession = {
  subject: { id: string; name: string | null; email: string | null };
  capabilities: {
    apps_read: boolean; conversations_read: boolean; messages_write: boolean;
    servers_read?: boolean; executors_read?: boolean; runs_read?: boolean;
    logs_read?: boolean; grant_management?: boolean;
  };
  project_access: boolean;
  session_id: string;
  csrf_token: string;
};

type BrowserApp = Pick<AppItem, 'id' | 'name' | 'slug' | 'created_at'> & { is_self?: boolean; can_create_conversation: boolean };
type BrowserConversation = Conversation & { can_send: boolean };

const uuid = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const appPath = new RegExp(`^/api/apps/(${uuid})(/access)?$`);
const conversationPath = new RegExp(`^/api/conversations/(${uuid})(/(?:access|messages|runs|models))?$`);
const activityPath = new RegExp(`^/api/conversations/(${uuid})/(?:queue|artifacts(?:/${uuid})?|runs/${uuid}/events)$`);
const cancelPath = new RegExp(`^/api/conversations/(${uuid})/queue/(${uuid})/cancel$`);
const archivePath = new RegExp(`^/api/conversations/(${uuid})/(?:archive|unarchive)$`);
const grantPath = new RegExp(`^/api/(tokens|conversations)/(${uuid})/exec-grants(?:/(${uuid}))?$`);
const executorPath = /^\/api\/executors\/nodes(?:\/([A-Za-z0-9_-]{1,128}))?$/;
const executorUpdatePath = /^\/api\/executors\/nodes\/([A-Za-z0-9_-]{1,128})\/self-update$/;

function executorProjection(row: WorkerNode): WorkerNode {
  return { id: row.id, name: row.name, status: row.status, last_seen_at: row.last_seen_at,
    connected_at: row.connected_at, disconnected_at: row.disconnected_at, heartbeat_age_seconds: row.heartbeat_age_seconds,
    system_default: row.system_default, enabled: row.enabled, conversation_enabled: row.conversation_enabled,
    protocol_version: row.protocol_version, worker_version: row.worker_version, tags: row.tags,
    runtime_validation_priority: row.runtime_validation_priority, runtime_validation_trusted: row.runtime_validation_trusted,
    assurance_class: row.assurance_class, assurance_state: row.assurance_state, browser_owned_by_actor: row.browser_owned_by_actor,
    browser_self_update_allowed: row.browser_self_update_allowed === true,
    // Never forward arbitrary JSON, host/Unix/path fields or helper results.
    capabilities: { docker: row.capabilities?.docker === true, firecracker: row.capabilities?.firecracker === true,
      event_streaming: row.capabilities?.event_streaming === true, cancellation: row.capabilities?.cancellation === true,
      infrastructure_helpers: row.capabilities?.infrastructure_helpers === true, self_update: row.capabilities?.self_update === true } };
}

function delegationQuery(url: URL): string {
  const id = url.searchParams.get('delegation_id');
  if (!id || !new RegExp(`^${uuid}$`).test(id) || url.search !== `?delegation_id=${id}`) return denied();
  return url.search;
}

function grantBody(body: unknown): Record<string, unknown> {
  const payload = strictBody(body, ['delegation_id', 'name', 'modes', 'executor_refs', 'ttl_minutes', 'max_uses']);
  if (typeof payload.delegation_id !== 'string' || !new RegExp(`^${uuid}$`).test(payload.delegation_id) ||
      typeof payload.name !== 'string' || !payload.name.trim() || new TextEncoder().encode(payload.name).length > 100 ||
      !Array.isArray(payload.modes) || !payload.modes.length || payload.modes.length > 2 ||
      payload.modes.some(mode => mode !== 'user' && mode !== 'privileged') ||
      new Set(payload.modes).size !== payload.modes.length ||
      !Array.isArray(payload.executor_refs) || !payload.executor_refs.length || payload.executor_refs.length > 64 ||
      payload.executor_refs.some(ref => typeof ref !== 'string' || !/^executor:[^\s/\\\0]{1,180}$/.test(ref)) ||
      new Set(payload.executor_refs).size !== payload.executor_refs.length ||
      typeof payload.ttl_minutes !== 'number' || !Number.isSafeInteger(payload.ttl_minutes) || payload.ttl_minutes < 1 ||
      payload.max_uses !== 1) return denied();
  // The server independently checks exact delegated selections and remaining
  // lease/cookie deadlines. Neither this shape check nor UI flags grant access.
  return payload;
}

function denied(): never {
  // Do not reflect the original request: it can contain a credential in a
  // query, fragment or unsupported body. No request is dispatched here.
  throw new ApiError('This operation is unavailable under the originating tool token.', 403, 'tool_browser_operation_unavailable');
}

function parsePath(path: string): URL {
  if (!path.startsWith('/api/') || path.includes('\\')) return denied();
  const url = new URL(path, 'https://browser.invalid');
  if (url.origin !== 'https://browser.invalid' || url.username || url.password || url.hash || url.pathname.includes('%') ||
      path.split('?')[0] !== url.pathname) return denied();
  return url;
}

function queryIs(url: URL, keys: string[]): boolean {
  const entries = [...url.searchParams];
  return entries.every(([key]) => keys.includes(key)) && new Set(entries.map(([key]) => key)).size === entries.length;
}

function appProjection(row: BrowserApp): AppItem {
  // These original table fields are the complete safe projection. Empty
  // container_names means unavailable in this audience, not a host inventory.
  return { id: row.id, name: row.name, slug: row.slug, created_at: row.created_at,
    is_self: row.is_self === true, container_names: [] };
}

function strictBody(body: unknown, fields: string[]): Record<string, unknown> {
  if (!body || typeof body !== 'object' || Array.isArray(body) ||
      Object.keys(body).some(key => !fields.includes(key))) return denied();
  return body as Record<string, unknown>;
}

/** Original components can name ordinary operations, but only this exact
 * mapping can dispatch them. This is not a REST proxy and has no OIDC recovery.
 * Every network request keeps the session identity captured at construction. */
export function createScopedApiClient(session: BrowserSession, onSessionLost: () => void): ApiClient {
  let closed = false;
  function checkIdentity() {
    if (closed || sessionStorage.getItem(browserSessionKey) !== session.session_id) {
      closed = true;
      onSessionLost();
      throw new ApiError('Browser session changed or expired. Open a tool-token login link again.', 401);
    }
  }
  async function request<T>(path: string, method = 'GET', body?: unknown, signal?: AbortSignal | null): Promise<T> {
    checkIdentity();
    signal?.throwIfAborted();
    const response = await fetch(path, {
      method, credentials: 'same-origin', cache: 'no-store', signal,
      headers: { 'Content-Type': 'application/json', 'X-Browser-Session-Id': session.session_id,
        ...(method === 'GET' ? {} : { 'X-Browser-CSRF': session.csrf_token }) },
      body: body === undefined ? undefined : JSON.stringify(body)
    });
    checkIdentity();
    signal?.throwIfAborted();
    if (!response.ok) {
      if ([401, 403, 404].includes(response.status)) { closed = true; onSessionLost(); }
      // Neither error bodies nor a reflected request become logs/UI content.
      throw new ApiError(response.status === 401 ? 'Browser session expired or revoked.' : 'Browser authority denied this operation.', response.status);
    }
    let value: unknown = null;
    if (response.status !== 204) {
      try { value = await response.json(); }
      catch { throw new ApiError('Browser response unavailable.', 502); }
    }
    checkIdentity();
    signal?.throwIfAborted();
    return value as T;
  }
  async function get<T>(path: string, options: Pick<RequestInit, 'signal'> = {}): Promise<T> {
    const url = parsePath(path);
    const signal = options.signal;
    let result: unknown;
    if (url.pathname === '/api/apps' && !url.search) {
      result = (await request<BrowserApp[]>('/api/browser/apps', 'GET', undefined, signal)).map(appProjection);
    } else if (new RegExp(`^/api/apps/${uuid}/conversations$`).test(url.pathname) && !url.search) {
      result = await request<BrowserConversation[]>(url.pathname.replace('/api/', '/api/browser/'), 'GET', undefined, signal);
    } else if (appPath.test(url.pathname) && !url.search) {
      const [, id, access] = appPath.exec(url.pathname)!;
      const row = await request<BrowserApp>(`/api/browser/apps/${id}`, 'GET', undefined, signal);
      result = access ? {
        app_id: row.id, project_name: row.name, project_slug: row.slug, role: null,
        capabilities: ['project:metadata:read', ...(row.can_create_conversation ? ['conversation:create'] : [])],
        admin_bypass: false, browser_operations: { conversations_read: session.capabilities.conversations_read }
      } satisfies AppAccessSummary : appProjection(row);
    } else if (url.pathname === '/api/conversations' && queryIs(url, ['limit', 'include_archived', 'search'])) {
      if (!session.capabilities.conversations_read ||
          (url.searchParams.has('limit') && url.searchParams.get('limit') !== '200') ||
          (url.searchParams.has('include_archived') && url.searchParams.get('include_archived') !== 'true')) return denied();
      const apps = await request<BrowserApp[]>('/api/browser/apps', 'GET', undefined, signal);
      const batches = await Promise.all(apps.map(app => request<BrowserConversation[]>(
        `/api/browser/apps/${app.id}/conversations`, 'GET', undefined, signal)));
      const search = (url.searchParams.get('search') ?? '').trim().toLowerCase();
      result = batches.flat().filter(row => (url.searchParams.has('include_archived') || !row.archived_at) &&
        (!search || row.title?.toLowerCase().includes(search)))
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at) || b.id.localeCompare(a.id)).slice(0, 200);
    } else if (conversationPath.test(url.pathname)) {
      const [, id, suffix = ''] = conversationPath.exec(url.pathname)!;
      if (suffix === '/messages') {
        if (!queryIs(url, ['limit']) || (url.search && url.searchParams.get('limit') !== '50')) return denied();
        result = (await request<ConversationMessage[]>(`/api/browser/conversations/${id}/messages`, 'GET', undefined, signal))
          .slice(-50).reverse();
      } else {
        if (url.search) return denied();
        if (suffix === '/models') {
          const rows = await request<{ id: string; name: string; model: string; available?: boolean; unavailable_reason?: unknown }[]>(`/api/browser/conversations/${id}/models`, 'GET', undefined, signal);
          result = rows.map(row => ({ id: row.id, name: row.name, model: row.model, available: row.available === true,
            ...(row.available === true ? {} : { unavailable_reason: row.unavailable_reason === 'source_unavailable'
              ? 'source_unavailable' : 'catalog_unavailable' }) }));
        } else {
          result = await request<Conversation | ConversationAccessSummary>(`/api/browser/conversations/${id}${suffix}`, 'GET', undefined, signal);
        }
      }
    } else if (activityPath.test(url.pathname) && !url.search) {
      result = await request(url.pathname.replace('/api/', '/api/browser/'), 'GET', undefined, signal);
    } else if (url.pathname === '/api/grant-targets' && !url.search) {
      result = await request('/api/browser/grant-targets', 'GET', undefined, signal);
    } else if (grantPath.test(url.pathname) && !grantPath.exec(url.pathname)![3]) {
      result = await request(url.pathname.replace('/api/', '/api/browser/') + delegationQuery(url), 'GET', undefined, signal);
    } else if (executorPath.test(url.pathname) && !url.search) {
      if (session.capabilities.executors_read !== true) return denied();
      const id = executorPath.exec(url.pathname)![1];
      result = id ? executorProjection(await request<WorkerNode>(`/api/browser/executors/nodes/${id}`, 'GET', undefined, signal))
        : (await request<WorkerNode[]>('/api/browser/executors', 'GET', undefined, signal)).map(executorProjection);
    } else {
      return denied();
    }
    checkIdentity();
    return result as T;
  }
  async function post<T>(path: string, body?: unknown): Promise<T> {
    const url = parsePath(path);
    if (url.search) return denied();
    if (cancelPath.test(url.pathname)) {
      if (body !== undefined) return denied();
      return request<T>(url.pathname.replace('/api/', '/api/browser/'), 'POST');
    }
    if (archivePath.test(url.pathname)) {
      if (body !== undefined) strictBody(body, []);
      await request(url.pathname.replace('/api/', '/api/browser/'), 'POST');
      // Reload the actual bounded projection, as the original list expects a
      // Conversation. The mutation response cannot expand that projection.
      return get<T>(`/api/conversations/${archivePath.exec(url.pathname)![1]}`);
    }
    if (executorUpdatePath.test(url.pathname)) {
      const payload = strictBody(body, ['drain_before_update']);
      if (typeof payload.drain_before_update !== 'boolean') return denied();
      return request<T>(url.pathname.replace('/api/', '/api/browser/'), 'POST', payload);
    }
    if (grantPath.test(url.pathname) && !grantPath.exec(url.pathname)![3]) {
      return request<T>(url.pathname.replace('/api/', '/api/browser/'), 'POST', grantBody(body));
    }
    if (url.pathname === '/api/conversations') {
      const payload = strictBody(body, ['title', 'app_id', 'app_ids', 'primary_app_id', 'interaction_mode', 'visibility']);
      const validApp = (id: unknown): id is string => typeof id === 'string' && new RegExp(`^${uuid}$`).test(id) &&
        id !== '00000000-0000-0000-0000-000000000000';
      if (typeof payload.title !== 'string' || (payload.interaction_mode !== 'chat' && payload.interaction_mode !== 'interactive_agent') ||
          (payload.visibility !== 'private' && payload.visibility !== 'project')) return denied();
      let primary: string | undefined;
      let appIds: string[] | undefined;
      if (Array.isArray(payload.app_ids) && payload.app_ids.length === 0 &&
          payload.app_id === undefined && payload.primary_app_id === undefined) {
        if (payload.visibility !== 'private') return denied();
        // The server resolves the self app and checks its normal project scope.
        appIds = [];
      } else if (payload.app_id !== undefined) {
        if (!validApp(payload.app_id) || payload.app_ids !== undefined || payload.primary_app_id !== undefined) return denied();
        primary = payload.app_id;
      } else {
        if (!validApp(payload.primary_app_id) || !Array.isArray(payload.app_ids) ||
            payload.app_ids.length < 2 || payload.app_ids.length > 64 || !payload.app_ids.every(validApp) ||
            payload.app_ids[0] !== payload.primary_app_id ||
            new Set(payload.app_ids.map(id => id.toLowerCase())).size !== payload.app_ids.length) return denied();
        primary = payload.primary_app_id;
        appIds = payload.app_ids;
      }
      const created = await request<{ id: string }>(primary ? `/api/browser/apps/${primary}/conversations` : '/api/browser/conversations', 'POST', {
        title: payload.title, ...(appIds ? { app_ids: appIds } : {}),
        ...(payload.interaction_mode === 'interactive_agent' ? { interaction_mode: 'interactive_agent' } : {}),
        ...(payload.visibility === 'project' ? { visibility: 'project' } : {})
      });
      // Reload real metadata; never manufacture a conversation from form input.
      return request<T>(`/api/browser/conversations/${created.id}`);
    }
    const match = conversationPath.exec(url.pathname);
    if (match?.[2] === '/messages') {
      const payload = strictBody(body, ['content', 'auto_process', 'model_id']);
      if (typeof payload.model_id !== 'string' || !new RegExp(`^${uuid}$`).test(payload.model_id) ||
          typeof payload.content !== 'string' || (payload.auto_process !== undefined && typeof payload.auto_process !== 'boolean')) return denied();
      return request<T>(`/api/browser/conversations/${match[1]}/messages`, 'POST', payload);
    }
    return denied();
  }
  async function del<T>(path: string): Promise<T> {
    const url = parsePath(path);
    if (grantPath.test(url.pathname) && grantPath.exec(url.pathname)![3]) {
      return request<T>(url.pathname.replace('/api/', '/api/browser/') + delegationQuery(url), 'DELETE');
    }
    return denied();
  }
  async function patch<T>(path: string, body?: unknown): Promise<T> {
    const url = parsePath(path);
    const match = conversationPath.exec(url.pathname);
    if (!match || match[2] || url.search) return denied();
    const payload = strictBody(body, ['title']);
    if (typeof payload.title !== 'string' || !payload.title.trim()) return denied();
    await request(`/api/browser/conversations/${match[1]}`, 'PATCH', payload);
    return get<T>(`/api/conversations/${match[1]}`);
  }
  return {
    mode: 'tool-browser', browserGrantManagement: session.capabilities.grant_management === true, get, post, del, patch,
    put: async () => denied(),
    blob: async () => denied(), upload: async () => denied()
  };
}
